import { dbAdd, dbDelete, dbGetAll, dbGetById, dbPut } from '../indexedDb.ts';

export interface LocalIndicator {
  id: number;
  key_result_id: number;
  month: string; // YYYY-MM
  value: number;
  created_at: string;
  updated_at?: string;
  server_id?: number;
  last_synced_at?: string;
  needs_sync?: boolean;
}

export function listIndicators() {
  return dbGetAll<LocalIndicator>('okr_indicators');
}

export async function listIndicatorsByKeyResult(keyResultId: number) {
  const all = await listIndicators();
  return all.filter((indicator) => indicator.key_result_id === keyResultId);
}

export async function getIndicatorByKeyResultMonth(keyResultId: number, month: string) {
  const all = await listIndicatorsByKeyResult(keyResultId);
  return all.find((indicator) => indicator.month === month);
}

export function getIndicatorById(id: number) {
  return dbGetById<LocalIndicator>('okr_indicators', id);
}

export async function getIndicatorByServerId(serverId: number) {
  const all = await listIndicators();
  return all.find((indicator) => indicator.server_id === serverId);
}

export async function createIndicator(
  data: Omit<LocalIndicator, 'id' | 'created_at' | 'updated_at' | 'server_id' | 'last_synced_at' | 'needs_sync'>
) {
  const now = new Date().toISOString();
  return dbAdd('okr_indicators', { ...data, created_at: now, needs_sync: true } as any);
}

export async function updateIndicator(id: number, data: Partial<LocalIndicator>) {
  const current = await dbGetById<LocalIndicator>('okr_indicators', id);
  if (!current) throw new Error('Indicator not found');
  const updated = {
    ...current,
    ...data,
    id,
    updated_at: data.updated_at ?? new Date().toISOString(),
    needs_sync: data.needs_sync ?? true,
  };
  await dbPut('okr_indicators', updated);
  return updated;
}

export function removeIndicator(id: number) {
  return dbDelete('okr_indicators', id);
}

export async function getIndicatorsNeedingSync(): Promise<LocalIndicator[]> {
  const all = await listIndicators();
  return all.filter((indicator) => indicator.needs_sync === true);
}

export async function markIndicatorSynced(id: number, serverId: number) {
  const current = await dbGetById<LocalIndicator>('okr_indicators', id);
  if (!current) throw new Error('Indicator not found');
  current.server_id = serverId;
  current.last_synced_at = new Date().toISOString();
  current.needs_sync = false;
  await dbPut('okr_indicators', current);
  return current;
}
